import { useState, useEffect } from "react";
import { apiClient } from "../api/client";
import type { EvaluationCriteria, EvaluationPeriod, User } from "../api/types";
import Card from "../components/Card";
import { useAuth } from "../context/AuthContext";

interface EvaluationFormData {
  evaluationType: "SELF" | "PEER";
  scores: Record<string, number>;
  comments: string;
  periodId: string;
}

function EvaluationForm() {
  const { user: currentUser } = useAuth();
  const [criteria, setCriteria] = useState<EvaluationCriteria[]>([]);
  const [evaluationPeriods, setEvaluationPeriods] = useState<EvaluationPeriod[]>([]);
  const [currentActivePeriod, setCurrentActivePeriod] = useState<EvaluationPeriod | null>(null);
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [evaluationType, setEvaluationType] = useState<"SELF" | "PEER">("SELF");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>("");

  const [formData, setFormData] = useState<Omit<EvaluationFormData, "periodId">>({
    evaluationType: "SELF",
    scores: {},
    comments: "",
  });

  // Fetch evaluation periods
  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const response = await apiClient.get<{ periods: EvaluationPeriod[] }>(
          "/evaluation-periods"
        );
        setEvaluationPeriods(response.periods || []);

        // Find current active period
        const activePeriod = response.periods?.find((p: EvaluationPeriod) => p.status === "ACTIVE");
        if (activePeriod) {
          setCurrentActivePeriod(activePeriod);
          setSelectedPeriodId(activePeriod.id);
        }
      } catch (err) {
        console.error("Failed to load periods:", err);
      }
    };
    loadPeriods();
  }, []);

  // Fetch criteria
  const fetchCriteria = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<{ criteria: EvaluationCriteria[] }>(
        "/evaluation-criteria"
      );
      const criteriaList = response.criteria || [];
      setCriteria(criteriaList);

      // Initialize scores object
      const scoresObj: Record<string, number> = {};
      criteriaList.forEach((c) => {
        scoresObj[c.id] = 3; // Default middle score
      });
      setFormData((prev) => ({
        ...prev,
        scores: scoresObj,
      }));

      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to load evaluation criteria");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCriteria();
  }, []);

  // Load team members when evaluation type is PEER
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (evaluationType !== "PEER" || !currentUser?.team_id) {
        setTeamMembers([]);
        setLoadingTeam(false);
        return;
      }

      setLoadingTeam(true);
      try {
        // Get team members from the same team (excluding current user)
        const teamUsers = await apiClient.get<User[]>("/users/my-team");
        setTeamMembers(teamUsers);
        // Auto-select first team member
        if (teamUsers.length > 0 && !selectedUserId) {
          setSelectedUserId(teamUsers[0].id);
        }
      } catch (err) {
        console.error("Failed to load team members:", err);
        setTeamMembers([]);
      } finally {
        setLoadingTeam(false);
      }
    };

    loadTeamMembers();
  }, [evaluationType, currentUser]);

  // Set user ID based on evaluation type
  useEffect(() => {
    if (evaluationType === "SELF" && currentUser) {
      setSelectedUserId(currentUser.id);
    }
  }, [evaluationType, currentUser]);

  // Handle score change
  const handleScoreChange = (criteriaId: string, score: number) => {
    setFormData((prev) => ({
      ...prev,
      scores: {
        ...prev.scores,
        [criteriaId]: score,
      },
    }));
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserId || !selectedPeriodId) {
      alert("Please fill in all required fields");
      return;
    }

    if (!currentUser?.id) {
      alert("User not authenticated");
      return;
    }

    try {
      setSubmitting(true);

      // Transform scores to object format for API
      const scoresObj: Record<string, number> = {};
      criteria.forEach((c) => {
        scoresObj[c.id] = formData.scores[c.id] || 0;
      });

      const payload = {
        userId: selectedUserId,
        evaluatorId: currentUser.id,
        evaluationType: evaluationType,
        periodId: selectedPeriodId,
        scores: scoresObj,
        comments: formData.comments || "",
      };

      await apiClient.post("/evaluations", payload);
      alert("Evaluation submitted successfully!");

      // Reset form
      setFormData({
        evaluationType: "SELF",
        scores: {},
        comments: "",
      });
      setEvaluationType("SELF");
      setSelectedUserId("");
      setSelectedPeriodId(currentActivePeriod?.id || "");
    } catch (err: any) {
      alert(err.message || "Failed to submit evaluation");
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate average score
  const averageScore =
    criteria.length > 0
      ? (Object.values(formData.scores).reduce((a, b) => a + b, 0) / criteria.length).toFixed(1)
      : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading evaluation form...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Create Evaluation</h2>
        <p className="text-sm text-slate-500 mt-1">Evaluate an employee's performance</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Evaluation Type - shown at top */}
        <Card title="Evaluation Type">
          <div className="space-y-1">
            <label className="text-sm text-slate-700 font-medium">Select Evaluation Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="evaluationType"
                  value="SELF"
                  checked={evaluationType === "SELF"}
                  onChange={(e) => setEvaluationType(e.target.value as "SELF" | "PEER")}
                  className="w-4 h-4"
                />
                <span className="text-sm text-slate-700">Self Assessment</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="evaluationType"
                  value="PEER"
                  checked={evaluationType === "PEER"}
                  onChange={(e) => setEvaluationType(e.target.value as "SELF" | "PEER")}
                  className="w-4 h-4"
                />
                <span className="text-sm text-slate-700">Peer Review</span>
              </label>
            </div>
          </div>
        </Card>

        {/* Evaluation Period Dropdown */}
        <Card title="Evaluation Period">
          <div className="space-y-1">
            <label className="text-sm text-slate-700 font-medium">Evaluation Period</label>
            <select
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={selectedPeriodId}
              onChange={(e) => setSelectedPeriodId(e.target.value)}
              required
            >
              <option value="">Select a period</option>
              {evaluationPeriods.map((period) => (
                <option key={period.id} value={period.id}>
                  {period.name} ({period.status})
                </option>
              ))}
            </select>
            {currentActivePeriod && (
              <p className="text-xs text-slate-500">
                Current active period: {currentActivePeriod.name}
              </p>
            )}
          </div>
        </Card>

        {/* Employee ID - shown conditionally */}
        <Card title="Employee">
          {evaluationType === "PEER" ? (
            <div className="space-y-1">
              <label className="text-sm text-slate-700 font-medium">Select Team Member</label>
              {loadingTeam ? (
                <div className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-500">
                  Loading team members...
                </div>
              ) : teamMembers.length > 0 ? (
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  required
                >
                  <option value="">Select a team member</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name} ({member.email})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-500">
                  No team members available
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              <label className="text-sm text-slate-700 font-medium">Employee</label>
              <div className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-700 bg-slate-50">
                {currentUser?.name} ({currentUser?.email})
              </div>
              <p className="text-xs text-slate-500">Self Assessment - you are evaluating yourself</p>
            </div>
          )}
        </Card>

        {/* Average Score Display */}
        <Card title="Score Overview">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm text-slate-700 font-medium">Average Score</label>
              <p className="text-xs text-slate-500 mt-1">Based on {criteria.length} criteria</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-indigo-600">
                {averageScore}
              </div>
              <div className="text-sm text-slate-500">/ 5</div>
            </div>
          </div>
        </Card>

        {/* Scoring Section */}
        <Card title={`Evaluation Criteria (${criteria.length} items)`}>
          <div className="space-y-6">
            {criteria.length === 0 ? (
              <p className="text-slate-500">
                No criteria defined. Please create evaluation criteria first.
              </p>
            ) : (
              criteria.map((c) => (
                <div key={c.id} className="border-b border-slate-200 pb-6 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">{c.name}</h4>
                      {c.description && (
                        <p className="text-sm text-slate-600 mt-1">{c.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
                          {c.category}
                        </span>
                        <span className="text-xs text-slate-500">Weight: {c.weight}x</span>
                      </div>
                    </div>
                  </div>

                  {/* Score Slider */}
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0"
                      max={c.maxScore}
                      step="1"
                      value={formData.scores[c.id] || 0}
                      onChange={(e) => handleScoreChange(c.id, parseInt(e.target.value))}
                      className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="text-center min-w-16">
                      <div className="text-2xl font-bold text-indigo-600">
                        {formData.scores[c.id] || 0}
                      </div>
                      <div className="text-xs text-slate-500">/ {c.maxScore}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Comments Section */}
        <Card title="Overall Comments">
          <textarea
            value={formData.comments}
            onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            rows={4}
            placeholder="Provide any additional feedback..."
          />
        </Card>

        {/* Submit Button */}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting || criteria.length === 0}
            className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {submitting ? "Submitting..." : "Submit Evaluation"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EvaluationForm;
